/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { ChangeEvent, FunctionComponent, JSX } from "react";
import { useCallback, useRef } from "react";

import { Locale, LocaleKeys } from "../../../viewModel/locale";
import { useAccessibilityFocusSync } from "../../hooks/useAccessibilityFocusSync";
import { useInputFocusSync } from "../../hooks/useInputFocusSync";
import type { PasswordInputProps } from "./PasswordInputProps";

import "./PasswordInput.scss";

const PasswordInput: FunctionComponent<PasswordInputProps> = (
  props: PasswordInputProps
): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09) for logging context
  const TAG: string = "PasswordInput";
  const {
    value,
    placeholder,
    onChange,
    onFocus,
    isPasswordVisible,
    onToggleVisibility,
    isFocused,
    isToggleFocused,
    isReadOnly
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  // VoiceView: real DOM focus follows the D-pad focus onto the Show/Hide toggle.
  const toggleRef = useRef<HTMLButtonElement>(null);
  useAccessibilityFocusSync(toggleRef, isToggleFocused === true);

  useInputFocusSync(inputRef, isFocused === true);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      onChange(event.target.value);
    },
    [onChange]
  );

  const passwordLabel: string = Locale.getString(
    LocaleKeys.SIGN_IN_PASSWORD_LABEL
  );

  return (
    <div
      className={
        "password-input" +
        (isFocused === true ? " password-input--focused" : "")
      }
    >
      <span className="password-input__label">{passwordLabel}</span>
      <div className="password-input__field-wrapper">
        <input
          ref={inputRef}
          className="password-input__field"
          type={isPasswordVisible ? "text" : "password"}
          value={value}
          placeholder={placeholder}
          aria-label={passwordLabel}
          onChange={handleChange}
          onFocus={onFocus}
          readOnly={isReadOnly === true}
        />
        <button
          ref={toggleRef}
          className={
            "password-input__toggle" +
            (isToggleFocused === true ? " password-input__toggle--focused" : "")
          }
          type="button"
          tabIndex={0}
          onClick={onToggleVisibility}
          aria-label={
            isPasswordVisible
              ? Locale.getString(
                  LocaleKeys.SIGN_IN_PASSWORD_HIDE_PASSWORD_LABEL
                )
              : Locale.getString(
                  LocaleKeys.SIGN_IN_PASSWORD_SHOW_PASSWORD_LABEL
                )
          }
        >
          {isPasswordVisible
            ? Locale.getString(LocaleKeys.SIGN_IN_PASSWORD_HIDE_BUTTON)
            : Locale.getString(LocaleKeys.SIGN_IN_PASSWORD_SHOW_BUTTON)}
        </button>
      </div>
    </div>
  );
};

export default PasswordInput;
